import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { ReferenceValue } from "../../../types/TenantInterfaces";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

export interface BrieGPTChat {
  Name: string;
  Instructions?: string;
  InstructionList?: Instruction[];
  id: string;
}

export interface Member {
  /** UUID */
  id: string;
  Member: ReferenceValue;
  Owner?: boolean;
}

export interface BrieGPTProject {
  id: string;
  Name: string;
  Chats: BrieGPTChat[];
  InstructionList: Instruction[];
  /** Only if the Private key is true */
  Members: Member[];
  ViewID: string;
  Record: { id: string; _ModuleAPIName: string };
  CreatedBy: string;
}

export type Instruction = {
  Instruction: string;
  Project?: boolean;
  id: string;
};

type ViewProps = {
  type: "view";
  viewID: string;
};

type RecordProps = {
  type: "record";
  recordID: string | undefined;
  moduleAPIName: string;
};

export type GetGPTItemsProps = ViewProps | RecordProps;

export function useBrieGPTChats(props: GetGPTItemsProps) {
  const tenantID = useAtomValue(tenantIDAtom);

  let queryIds: string[];
  if (props.type === "view") {
    queryIds = [props.viewID];
  } else {
    queryIds = [props.recordID || ""];
  }

  const query = useQuery({
    queryKey: [tenantID, ...queryIds, "BrieGPTChats"],
    queryFn: () => {
      return BrieConversationAPI.getBrieGPTChats({
        ...props,
        operation: "chats",
      }).then((res) => res.data.data);
    },
    enabled:
      props.type === "view" ? Boolean(props.viewID) : Boolean(props.recordID),
  });

  return {
    chats: query.data?.records as BrieGPTChat[],
    chatsLoading: query.isLoading,
  };
}

export function useBrieGPTProjects(
  props: GetGPTItemsProps & { tab: BrieGPTTabs }
) {
  const tenantID = useAtomValue(tenantIDAtom);

  let queryIds: string[];
  if (props.type === "view") {
    queryIds = [props.viewID];
  } else {
    queryIds = [props.recordID || ""];
  }

  const query = useQuery({
    queryKey: [tenantID, ...queryIds, "BrieGPTProjects", props.tab],
    queryFn: () => {
      return BrieConversationAPI.getBrieGPTChats({
        ...props,
        operation: "projects",
        filterType: props.tab,
      }).then((res) => res.data.data);
    },
    enabled:
      (props.type === "view"
        ? Boolean(props.viewID)
        : Boolean(props.recordID)) && !!props.tab,
  });

  return {
    projects: query.data?.records as BrieGPTProject[],
    projectsLoading: query.isLoading,
  };
}
